import React from "react";

import { CheckCircle, CloudUpload, ErrorOutline } from "@mui/icons-material";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { AutosaveStatus } from "@/components/alerts/add-v2/persistence/useAutosave";

interface SaveIndicatorProps {
  status: AutosaveStatus;
  savedAt: Date | null;
  /** Sends the outstanding change again. Only reachable while `status` is `"error"`. */
  onRetry: () => void;
  /**
   * Icon only, with the line moved into a tooltip. What the phone bar uses: the line is the least
   * urgent thing on it — autosave is a promise, not a decision — so it is what gives up its width
   * when the bar has to fit a discard, a preview toggle and Finalize as well.
   */
  compact?: boolean;
}

/**
 * What autosave is doing, in one line.
 *
 * It is always present, including before the first save. The prototype's version says "Draft saved"
 * from the start, which is not true of a form nobody has typed into; the idle line here promises the
 * behaviour instead of claiming a save that has not happened.
 *
 * The failure line is the only one with a control. `useAutosave` has already retried once by the
 * time this shows, so the button is the third attempt rather than the second — and the content is
 * still dirty behind it, which is why nothing is lost if the officer ignores it and keeps typing.
 */
export const SaveIndicator: React.FC<SaveIndicatorProps> = ({
  status,
  savedAt,
  onRetry,
  compact = false,
}) => {
  const { t } = useTranslation("bulletin/create-v2");

  const time = savedAt?.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (status === "error") {
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}
      >
        <ErrorOutline fontSize="small" color="error" />
        {/* Compact drops the line but never the retry: it is the only control here. */}
        {!compact && (
          <Typography variant="caption" color="error">
            {t("save.failed")}
          </Typography>
        )}
        <Button size="small" onClick={onRetry} sx={{ minWidth: 0, px: 1 }}>
          {t("save.retry")}
        </Button>
      </Box>
    );
  }

  const [icon, label] =
    status === "saving"
      ? [
          <CloudUpload key="i" fontSize="small" color="disabled" />,
          t("save.saving"),
        ]
      : savedAt
        ? [
            <CheckCircle key="i" fontSize="small" color="disabled" />,
            t("save.saved_at", { time }),
          ]
        : [
            <CheckCircle key="i" fontSize="small" color="disabled" />,
            t("save.automatic"),
          ];

  if (compact) {
    return (
      <Tooltip title={label}>
        <Box sx={{ display: "flex", alignItems: "center" }} aria-label={label}>
          {icon}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      {icon}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};
